import { defaultHeaders } from '@/src/utilities/fetch-utilities';
import { createResult } from '@/src/utilities/result';

import { mutationTypes } from './mutations';

export const actionTypes = {
  LOAD_SEARCH_RESULTS: 'LOAD_SEARCH_RESULTS',
  LOAD_SEARCH_SETTINGS: 'LOAD_SEARCH_SETTINGS',
  INITIALIZE_FILTER_STATE: 'INITIALIZE_FILTER_STATE',
  REFRESH_FILTER_STATE: 'REFRESH_FILTER_STATE',
};

/**
 * @param {keyof actionTypes} actionType
 */
export function action(actionType) {
  return `search/${actionType}`;
}

/**
 * @type {import('vuex').ActionTree<SearchState, SearchState>}
 */
export const actions = {
  /**
   * Sets the initial filter state from the URL and retrieves the filter content from the API
   * This is used when the app first loads and we do not yet have filter values to toggle/use for a search request
   */
  async [actionTypes.INITIALIZE_FILTER_STATE]({ commit, dispatch, state }) {
    const { searchTerm, taxonomyIds } = parseURLFilterState(
      state.queryKeyIdentifiers,
      state.queryKeyTerm,
    );

    if (!taxonomyIds.length && !searchTerm) {
      return;
    }

    if (searchTerm) {
      commit('SET_SEARCH_TERM', searchTerm);
    }

    await dispatch('LOAD_SEARCH_SETTINGS');

    if (taxonomyIds.length) {
      commit('SET_SELECTED_FILTERS', taxonomyIds);
    }
  },

  /**
   * Refreshes the filter state from the URL and then executes a search request to the API
   * This is used when the page is loaded from the bfcache (https://web.dev/bfcache/)
   * and we want to ensure the app state matches the URL, which is the source of truth
   */
  async [actionTypes.REFRESH_FILTER_STATE]({ commit, dispatch, state }) {
    const { taxonomyIds, searchTerm } = parseURLFilterState(
      state.queryKeyIdentifiers,
      state.queryKeyTerm,
    );

    commit('SET_SEARCH_TERM', searchTerm);

    commit('SET_SELECTED_FILTERS', taxonomyIds);

    await dispatch('LOAD_SEARCH_RESULTS', true);
  },

  /**
   * Retrieves the default (empty) filter state from the API which allows
   * the app to match up filter content from the URL (taxonomy identifiers)
   * with the filter values that should be sent to the API for a search request
   */
  async [actionTypes.LOAD_SEARCH_SETTINGS]({ commit, state }) {
    commit(mutationTypes.SET_IS_LOADING, true);

    const { taxonomyRootNodeGUID } = state.request;

    const result = await createResult(
      fetch(`/api/search/${state.culture}/settings`, {
        method: 'POST',
        body: JSON.stringify({ taxonomyRootNodeGUID }),
        headers: defaultHeaders(),
      }).then((resp) => resp.json()),
    );

    commit(mutationTypes.SET_IS_LOADING, false);

    if (result.isFailure) {
      return result;
    }

    commit(mutationTypes.SET_FACET_GROUPS, result.value.groups);
  },

  /**
   * Makes a search request to the API using the application's current state
   * and then updates the URL with that state if the request was successful
   */
  async [actionTypes.LOAD_SEARCH_RESULTS](
    { commit, state },
    /** @type {boolean} */ skipURLUpdate,
  ) {
    if (!state.abortController.signal.aborted) {
      state.abortController.abort();
    }

    commit(mutationTypes.RESET_ABORT_CONTROLLER);

    commit(mutationTypes.SET_IS_LOADING, true);

    const result = await createResult(
      fetch(`/api/search/${state.culture}`, {
        method: 'POST',
        body: JSON.stringify(state.request),
        headers: defaultHeaders(),
        signal: state.abortController.signal,
      }).then((resp) => resp.json()),
    );

    commit(mutationTypes.SET_IS_LOADING, false);

    if (result.isFailure) {
      return result;
    }

    commit(mutationTypes.SET_RESPONSE, result.value);

    if (skipURLUpdate) {
      return;
    }

    // After search has returned and UI is updated successfully,
    // change the querystring via history.pushState to reflect currently-applied filters.
    // Save filter state with alphanumeric facet identifiers in the querystring.
    const appliedIdentifiers = [];
    const appliedDisplayNames = [];
    const appliedGroupIds = { groups: {} };
    for (const g in result.value.groups) {
      for (const f in result.value.groups[g].facets) {
        if (result.value.groups[g].facets[f].isSelected) {
          appliedIdentifiers.push(result.value.groups[g].facets[f].identifier);
          appliedDisplayNames.push(result.value.groups[g].facets[f].name);
          appliedGroupIds.groups[result.value.groups[g].value] =
            result.value.groups[g].facets[f].value;
        }
      }
    }

    const url = new URL(window.location.href);

    // Clear out querystring parameters
    url.searchParams.delete(state.queryKeyIdentifiers);
    url.searchParams.delete(state.queryKeyTerm);

    // Append currently active parameters
    appliedIdentifiers.forEach((n) =>
      url.searchParams.append(state.queryKeyIdentifiers, n),
    );
    if (state.request.term) {
      url.searchParams.append(state.queryKeyTerm, state.request.term);
    }

    // The 'appliedGroupIds' data is optional in the history entry, but it does save a comprehensive collection of filter state
    // in the format appliedGroupIds[{groupTaxonomyNodeId}] = [{taxonomyNodeId1},{taxonomyNodeId2}]
    history.pushState(
      appliedGroupIds,
      appliedDisplayNames.join(', '),
      url.toString(),
    );
  },
};

/**
 * @param {string} queryKeyIdentifiers
 * @param {string} queryKeyTerm
 */
function parseURLFilterState(queryKeyIdentifiers, queryKeyTerm) {
  const searchParams = new URL(window.location.href).searchParams;

  // Look for querystring taxonomy identifiers in the legacy format ('ti=value1,value2').
  // Otherwise, the page will evaluate the new querystring array format
  // (using separate ti=value&ti=value parameters for array definition)
  const qsIds = searchParams.get(queryKeyIdentifiers);

  const taxonomyIds =
    qsIds && qsIds.includes(',')
      ? qsIds.split(',')
      : searchParams.getAll(queryKeyIdentifiers) || [];

  // Look for persisted search term in querystring
  const searchTerm = searchParams.get(queryKeyTerm) || '';

  return { taxonomyIds, searchTerm };
}

/**
 * @typedef {import('./state').SearchState} SearchState
 */
